import { Param } from "../pages/MainPage"

export class QBPModel {
    name: string
    delta: number
    price: number
    cardinality: number
    price_pempem: number
    price_highlighted: boolean

    constructor(q: any) {
        this.name = q.name
        this.delta = q.delta
        this.price = q.price
        this.cardinality = q.cardinality
        this.price_pempem = q.price_pempem
        this.price_highlighted = q.price_highlighted
    }

    static build(q: any): QBPModel {
        return new QBPModel(q)
    }
}

export class PriceModel {

    id: number
    pricePerUnit?: number
    millId?: number
    millName?: string
    subdistrict?: string
    village?: string
    coordinates?: number[]
    qbp_prices?: Array<QBPModel>
    program_name?: string
    minimum_quality_level?: string
    require_spb?: string
    standard_grading_ratio?: number
    sorting_strictness?: string

    constructor(p: any) {
        this.id = p.id
        this.pricePerUnit = p.price
        this.millId = p.millId
        this.millName = p.millName
        this.subdistrict = p.subdistrict
        this.village = p.village
        this.coordinates = p.coordinates
        this.qbp_prices = p.qbp_prices?.map((q: any) => QBPModel.build(q))
        this.program_name = p.program_name
        this.minimum_quality_level = p.minimum_quality_level
        this.require_spb = p.require_spb
        this.standard_grading_ratio = p.standard_grading_ratio
        this.sorting_strictness = p.sorting_strictness
    }

    isProgramPrice() {
        return this.program_name === "unilever-v02"
    }

    toParam(): Param[] {
        return [
            { key: "id", value: this.id },
            { key: "price", value: this.pricePerUnit },
            { key: "mill_id", value: this.millId },
            { key: "mill_name", value: encodeURIComponent(this.millName ?? "") },
            { key: "subdistrict", value: this.subdistrict },
            { key: "village", value: this.village },
            { key: "qbp", value: encodeURIComponent(JSON.stringify(this.qbp_prices)) },
            { key: "program_name", value: this.program_name },
            { key: "minimum_quality_level", value: encodeURIComponent(JSON.stringify(this.minimum_quality_level)) },
            { key: "require_spb", value: encodeURIComponent(JSON.stringify(this.require_spb)) },
            { key: "standard_grading_ratio", value: encodeURIComponent(JSON.stringify(this.standard_grading_ratio)) },
            { key: "sorting_strictness", value: encodeURIComponent(JSON.stringify(this.sorting_strictness)) },
        ]
    }

    static build(p: any) {
        return new PriceModel(p)
    }

    static buildEmptyPrice() {
        return new PriceModel({ id: -1, pricePerUnit: -1 })
    }

    static buildLocalPrice(p: number) {
        return new PriceModel({ id: 0, pricePerUnit: p })
    }

    static buildFromFeature(p: any, g: any) {
        // mapbox can hand nested arrays back from a source as JSON strings, so tolerate both.
        let qbp: any[] | undefined = [];
        try {
            qbp = typeof p.tiers === 'string' ? JSON.parse(p.tiers) : p.tiers
        } catch (e) {
            qbp = undefined;
        }

        return new PriceModel({
            id: p.id,
            price: p.price_per_kg,
            millId: p.mill_id,
            millName: p.mill_full_name ?? p.mill_name,
            subdistrict: p.location_subdistrict,
            village: p.location_village,
            coordinates: g.coordinates,
            qbp_prices: qbp || [],
            program_name: p.program_name,
            minimum_quality_level: p.minimum_quality_level,
            require_spb: p.require_spb,
            standard_grading_ratio: p.standard_grading_ratio,
            sorting_strictness: p.sorting_strictness
        });
    }

    static buildFromParam(param: any, fetchedPrices?: PriceModel[]) {
        const parseParam = (key: string, defaultValue: any = null) => {
            try {
                return JSON.parse(param.get(key));
            } catch {
                return defaultValue;
            }
        };

        const qbp = parseParam("qbp", undefined);
        const minimum_quality_level = parseParam("minimum_quality_level");
        const require_spb = parseParam("require_spb");
        const standard_grading_ratio = parseParam("standard_grading_ratio");
        const sorting_strictness = parseParam("sorting_strictness");

        let priceData: any = {
            id: param.get("id"),
            price: param.get("price"),
            millId: param.get("mill_id"),
            millName: decodeURIComponent(param.get("mill_name") ?? ""),
            subdistrict: param.get("subdistrict"),
            village: param.get("village"),
            qbp_prices: qbp,
            program_name: param.get('program_name'),
            minimum_quality_level: minimum_quality_level,
            require_spb: require_spb,
            standard_grading_ratio: standard_grading_ratio,
            sorting_strictness: sorting_strictness
        };

        // Validate and overwrite with fetched data if available
        if (fetchedPrices && priceData.id) {
            const matchingPrice = fetchedPrices.find(p => p.id === parseInt(priceData.id));
            if (matchingPrice) {
                // Overwrite with fetched data to ensure accuracy
                priceData = {
                    id: matchingPrice.id,
                    price: matchingPrice.pricePerUnit,
                    millId: matchingPrice.millId,
                    millName: matchingPrice.millName,
                    subdistrict: matchingPrice.subdistrict,
                    village: matchingPrice.village,
                    qbp_prices: matchingPrice.qbp_prices,
                    program_name: matchingPrice.program_name,
                    minimum_quality_level: matchingPrice.minimum_quality_level,
                    require_spb: matchingPrice.require_spb,
                    standard_grading_ratio: matchingPrice.standard_grading_ratio,
                    sorting_strictness: matchingPrice.sorting_strictness
                };
            }
        }

        return new PriceModel(priceData);
    }

    location() {
        return `${this.subdistrict}, ${this.village}`
    }
}